import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** How long an image stays on screen in the viewer before advancing, in ms. */
const IMAGE_DURATION = 5000;

/** Minimum pointer travel, in px, that counts as a swipe in the viewer. */
const SWIPE_THRESHOLD = 50;

/**
 * Horizontal carousel of product images and videos with a centred "focus" tile, plus a
 * full-screen story-style viewer.
 *
 * The centred tile is marked active; when auto-play is on, only the active video previews (muted)
 * while the carousel is on screen. Any tile opens the viewer at that position.
 *
 * @typedef {object} ProductMediaCarouselRefs
 * @property {HTMLElement} track - The scrolling container
 * @property {HTMLDialogElement} viewer - Full-screen viewer dialog
 * @property {HTMLElement} stage - Viewer element that holds the current media
 * @property {HTMLElement} segments - Viewer progress segments container
 * @property {HTMLButtonElement} pauseButton - Viewer pause / play toggle
 * @property {HTMLButtonElement} muteButton - Viewer mute toggle
 * @property {HTMLElement[]} [items] - Media tiles
 * @property {HTMLElement} [progressBar] - Carousel position bar
 * @property {HTMLElement} [counter] - Carousel "n / total" counter
 * @property {HTMLButtonElement} [previous] - Previous arrow
 * @property {HTMLButtonElement} [next] - Next arrow
 *
 * @extends {Component<ProductMediaCarouselRefs>}
 */
class ProductMediaCarousel extends Component {
  requiredRefs = ['track', 'viewer', 'stage', 'segments', 'pauseButton', 'muteButton'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;

  /** @type {IntersectionObserver | undefined} */
  #posterObserver;

  #activeIndex = 0;
  #isVisible = false;
  #scrollFrame = 0;

  // Viewer state
  #viewerIndex = 0;
  #viewerPaused = false;
  #viewerMuted = false;
  #imageStart = 0;
  #imageElapsed = 0;
  #imageFrame = 0;
  /** @type {HTMLVideoElement | null} */
  #viewerVideo = null;
  /** @type {HTMLElement | null} */
  #opener = null;
  /** @type {{ x: number, y: number } | null} */
  #pointerStart = null;
  #swiped = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /* Carousel */

  scrollPrevious() {
    this.#scrollToIndex(this.#activeIndex - 1);
  }

  scrollNext() {
    this.#scrollToIndex(this.#activeIndex + 1);
  }

  /**
   * Opens the viewer at the tile that was activated.
   * @param {MouseEvent} event
   */
  openViewer(event) {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const item = target.closest('.product-media-carousel__item');
    const index = this.#items().findIndex((candidate) => candidate === item);
    if (index < 0) return;

    this.#opener = target instanceof HTMLElement ? target : null;
    this.#openViewerAt(index);
  }

  #setup() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { track, viewer } = this.refs;

    track.addEventListener('scroll', () => this.#queueActiveUpdate(), { passive: true, signal });
    window.addEventListener('resize', () => this.#queueActiveUpdate(), { passive: true, signal });

    for (const item of this.#items()) {
      const video = item.querySelector('video');
      if (!video) continue;

      video.addEventListener('play', () => item.classList.add('is-playing'), { signal });
      video.addEventListener('pause', () => item.classList.remove('is-playing'), { signal });
      video.addEventListener(
        'timeupdate',
        () => {
          if (video.duration) item.style.setProperty('--pmc-video-progress', String(video.currentTime / video.duration));
        },
        { signal }
      );
    }

    viewer.addEventListener('close', () => this.#handleViewerClose(), { signal });
    viewer.addEventListener('pointerdown', (event) => this.#handlePointerDown(event), { signal });
    viewer.addEventListener('pointerup', (event) => this.#handlePointerUp(event), { signal });
    viewer.addEventListener('pointercancel', () => (this.#pointerStart = null), { signal });

    this.#visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isVisible = Boolean(entry?.isIntersecting);
        this.#syncInlinePlayback();
      },
      { threshold: 0.35 }
    );
    this.#visibilityObserver.observe(this);

    // Posters load shortly before the carousel scrolls into view. A carousel hidden at this breakpoint never intersects.
    this.#posterObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;

        for (const video of this.querySelectorAll('video[data-poster]')) {
          if (!(video instanceof HTMLVideoElement) || !video.dataset.poster) continue;
          video.poster = video.dataset.poster;
          video.removeAttribute('data-poster');
        }
        this.#posterObserver?.disconnect();
      },
      { rootMargin: '300px 0px' }
    );
    this.#posterObserver.observe(this);

    this.#updateActive();
    this.setAttribute('data-ready', '');
  }

  #teardown() {
    this.#abortController?.abort();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = undefined;
    this.#posterObserver?.disconnect();
    this.#posterObserver = undefined;
    cancelAnimationFrame(this.#scrollFrame);
    this.#stopViewerMedia();
    // The close listener is already aborted, so release the scroll lock here.
    if (this.refs.viewer?.open) {
      this.refs.viewer.close();
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  /** @returns {HTMLElement[]} */
  #items() {
    return this.refs.items ?? [];
  }

  #queueActiveUpdate() {
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => this.#updateActive());
  }

  /** Marks the tile closest to the track's centre as active and refreshes the controls. */
  #updateActive() {
    const items = this.#items();
    if (!items.length) return;

    const trackRect = this.refs.track.getBoundingClientRect();
    const centre = trackRect.left + trackRect.width / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - centre);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.#activeIndex = closestIndex;
    items.forEach((item, index) => item.classList.toggle('is-active', index === closestIndex));

    const { progressBar, counter, previous, next } = this.refs;
    if (progressBar) progressBar.style.setProperty('--pmc-progress', String((closestIndex + 1) / items.length));
    if (counter) counter.textContent = `${closestIndex + 1} / ${items.length}`;
    if (previous) previous.disabled = closestIndex === 0;
    if (next) next.disabled = closestIndex === items.length - 1;

    this.#syncInlinePlayback();
  }

  /** Previews only the active video, muted, while the carousel is visible and the viewer is closed. */
  #syncInlinePlayback() {
    const autoplay = this.dataset.autoplayOnScroll === 'true' && !prefersReducedMotion();
    const viewerOpen = this.refs.viewer?.open;

    this.#items().forEach((item, index) => {
      const video = item.querySelector('video');
      if (!video) return;

      const isActive = index === this.#activeIndex;
      // Only fetch while on screen, so a copy hidden at this breakpoint never downloads video.
      if (this.#isVisible && (isActive || Math.abs(index - this.#activeIndex) === 1)) loadVideoSources(video);

      if (autoplay && isActive && this.#isVisible && !viewerOpen) {
        video.muted = true;
        video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  /** @param {number} index */
  #scrollToIndex(index) {
    const item = this.#items()[index];
    if (!item) return;

    item.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  /* Viewer */

  /** Tap zone handler; ignores the click that can follow a swipe. */
  tapPrevious() {
    if (this.#consumeSwipe()) return;
    this.viewerPrevious();
  }

  /** Tap zone handler; ignores the click that can follow a swipe. */
  tapNext() {
    if (this.#consumeSwipe()) return;
    this.viewerNext();
  }

  viewerPrevious() {
    this.#showViewerItem(this.#viewerIndex - 1);
  }

  viewerNext() {
    if (this.#viewerIndex >= this.#items().length - 1) {
      this.closeViewer();
      return;
    }
    this.#showViewerItem(this.#viewerIndex + 1);
  }

  closeViewer() {
    if (this.refs.viewer.open) this.refs.viewer.close();
  }

  toggleViewerPause() {
    this.#setViewerPaused(!this.#viewerPaused);
  }

  toggleViewerMute() {
    this.#viewerMuted = !this.#viewerMuted;
    if (this.#viewerVideo) this.#viewerVideo.muted = this.#viewerMuted;
    this.#syncViewerControls();
  }

  /** @param {KeyboardEvent} event */
  handleViewerKeydown(event) {
    const isRtl = getComputedStyle(this).direction === 'rtl';

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      isRtl ? this.viewerPrevious() : this.viewerNext();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      isRtl ? this.viewerNext() : this.viewerPrevious();
    }
  }

  /** @param {number} index */
  #openViewerAt(index) {
    const { viewer, segments } = this.refs;
    const count = this.#items().length;

    segments.replaceChildren(
      ...Array.from({ length: count }, () => {
        const segment = document.createElement('span');
        segment.className = 'product-media-viewer__segment';
        const fill = document.createElement('span');
        fill.className = 'product-media-viewer__segment-fill';
        segment.append(fill);
        return segment;
      })
    );

    // Opening the viewer is an explicit request to watch, so play with sound and auto-advance,
    // except for visitors who prefer reduced motion.
    this.#viewerMuted = false;
    this.#viewerPaused = prefersReducedMotion();

    viewer.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.#syncInlinePlayback();
    this.#showViewerItem(index);
  }

  /** @param {number} index */
  #showViewerItem(index) {
    const items = this.#items();
    const item = items[Math.max(0, Math.min(index, items.length - 1))];
    if (!item) return;

    this.#viewerIndex = items.indexOf(item);
    this.#stopViewerMedia();
    this.#updateSegments(0);

    const source = item.querySelector('.product-media-carousel__media');
    let media;

    if (source instanceof HTMLVideoElement) {
      media = this.#createViewerVideo(source);
    } else if (source instanceof HTMLImageElement) {
      media = /** @type {HTMLImageElement} */ (source.cloneNode());
      media.className = 'product-media-viewer__media';
      media.loading = 'eager';
      media.sizes = '(min-width: 750px) 60vh, 100vw';
      this.#imageElapsed = 0;
      if (!this.#viewerPaused) this.#startImageTimer();
    }

    if (media) this.refs.stage.replaceChildren(media);
    this.#syncViewerControls();

    // Keep the carousel in step so closing returns to the last viewed tile.
    this.#scrollToIndex(this.#viewerIndex);
  }

  /**
   * @param {HTMLVideoElement} source
   * @returns {HTMLVideoElement}
   */
  #createViewerVideo(source) {
    const video = document.createElement('video');
    video.className = 'product-media-viewer__media';
    video.playsInline = true;
    video.preload = 'auto';
    video.muted = this.#viewerMuted;
    const poster = source.poster || source.dataset.poster;
    if (poster) video.poster = poster;
    const label = source.getAttribute('aria-label');
    if (label) video.setAttribute('aria-label', label);

    for (const original of source.querySelectorAll('source')) {
      const url = original.getAttribute('src') ?? original.getAttribute('data-src');
      if (!url) continue;
      const clone = document.createElement('source');
      clone.src = url;
      clone.type = original.getAttribute('type') ?? '';
      video.append(clone);
    }

    video.addEventListener('timeupdate', () => {
      if (video.duration) this.#updateSegments(video.currentTime / video.duration);
    });
    video.addEventListener('ended', () => this.viewerNext());

    this.#viewerVideo = video;
    if (!this.#viewerPaused) this.#playViewerVideo(video);
    return video;
  }

  /**
   * Plays with sound, falling back to muted where the browser blocks audible autoplay.
   * @param {HTMLVideoElement} video
   */
  #playViewerVideo(video) {
    video.play().catch(() => {
      if (video.muted) return;
      this.#viewerMuted = true;
      video.muted = true;
      this.#syncViewerControls();
      video.play().catch(() => {});
    });
  }

  #startImageTimer() {
    cancelAnimationFrame(this.#imageFrame);
    this.#imageStart = performance.now() - this.#imageElapsed;

    const tick = () => {
      this.#imageElapsed = performance.now() - this.#imageStart;
      const progress = Math.min(this.#imageElapsed / IMAGE_DURATION, 1);
      this.#updateSegments(progress);

      if (progress >= 1) {
        this.viewerNext();
        return;
      }
      this.#imageFrame = requestAnimationFrame(tick);
    };

    this.#imageFrame = requestAnimationFrame(tick);
  }

  /** @param {boolean} paused */
  #setViewerPaused(paused) {
    this.#viewerPaused = paused;

    if (this.#viewerVideo) {
      if (paused) this.#viewerVideo.pause();
      else this.#playViewerVideo(this.#viewerVideo);
    } else if (paused) {
      cancelAnimationFrame(this.#imageFrame);
    } else {
      this.#startImageTimer();
    }

    this.#syncViewerControls();
  }

  /** @param {number} currentProgress - Progress of the current item, 0 to 1 */
  #updateSegments(currentProgress) {
    const fills = this.refs.segments.querySelectorAll('.product-media-viewer__segment-fill');

    fills.forEach((fill, index) => {
      let value = 0;
      if (index < this.#viewerIndex) value = 1;
      else if (index === this.#viewerIndex) value = currentProgress;
      if (fill instanceof HTMLElement) fill.style.setProperty('--pmc-segment-progress', String(value));
    });
  }

  #syncViewerControls() {
    const { viewer, pauseButton, muteButton } = this.refs;

    viewer.classList.toggle('is-paused', this.#viewerPaused);
    viewer.classList.toggle('is-muted', this.#viewerMuted);

    const pauseLabel = this.#viewerPaused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause;
    if (pauseLabel) pauseButton.setAttribute('aria-label', pauseLabel);

    muteButton.hidden = !this.#viewerVideo;
    const muteLabel = this.#viewerMuted ? muteButton.dataset.labelUnmute : muteButton.dataset.labelMute;
    if (muteLabel) muteButton.setAttribute('aria-label', muteLabel);
  }

  #stopViewerMedia() {
    cancelAnimationFrame(this.#imageFrame);
    this.#imageElapsed = 0;

    if (this.#viewerVideo) {
      this.#viewerVideo.pause();
      this.#viewerVideo.removeAttribute('src');
      this.#viewerVideo.replaceChildren();
      this.#viewerVideo.load();
      this.#viewerVideo = null;
    }

    this.refs.stage?.replaceChildren();
  }

  #handleViewerClose() {
    this.#stopViewerMedia();
    document.documentElement.removeAttribute('scroll-lock');
    this.#syncInlinePlayback();

    // Return focus to the tile for the item last viewed, falling back to the one that opened the viewer.
    const lastViewed = this.#items()[this.#viewerIndex]?.querySelector('.product-media-carousel__open');
    const focusTarget = lastViewed instanceof HTMLElement ? lastViewed : this.#opener;
    focusTarget?.focus({ preventScroll: true });
    this.#opener = null;
  }

  #consumeSwipe() {
    const swiped = this.#swiped;
    this.#swiped = false;
    return swiped;
  }

  /** @param {PointerEvent} event */
  #handlePointerDown(event) {
    this.#swiped = false;
    if (event.pointerType === 'mouse') return;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
  }

  /** @param {PointerEvent} event */
  #handlePointerUp(event) {
    const start = this.#pointerStart;
    this.#pointerStart = null;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    if (dy > SWIPE_THRESHOLD * 2 && Math.abs(dy) > Math.abs(dx)) {
      this.closeViewer();
      return;
    }

    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;

    // A swipe should not also trigger the tap zone underneath it.
    this.#swiped = true;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? dx > 0 : dx < 0;
    forward ? this.viewerNext() : this.viewerPrevious();
  }
}

/**
 * Moves deferred `data-src` sources into place the first time a video is needed.
 * @param {HTMLVideoElement} video
 */
function loadVideoSources(video) {
  const sources = video.querySelectorAll('source[data-src]');
  if (!sources.length) return;

  for (const source of sources) {
    if (!(source instanceof HTMLSourceElement) || !source.dataset.src) continue;
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
  }

  video.preload = 'metadata';
  video.load();
}

if (!customElements.get('product-media-carousel')) {
  customElements.define('product-media-carousel', ProductMediaCarousel);
}
